import { useState } from 'react';

export function Footer() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new URLSearchParams();
      formData.append('name', name);
      formData.append('email', email);
      formData.append('message', message);

      const response = await fetch('https://readdy.ai/api/form/d4k5iousn5v8oqu6pbc0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setName('');
        setEmail('');
        setMessage('');
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subscribeEmail.trim()) {
      setSubscribeStatus('error');
      return;
    }

    setSubscribeStatus('idle');

    try {
      const formData = new URLSearchParams();
      formData.append('email', subscribeEmail);

      const response = await fetch('https://readdy.ai/api/form/d4k5iousn5v8oqu6pbc0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubscribeStatus('success');
        setSubscribeEmail('');
        setTimeout(() => setSubscribeStatus('idle'), 5000);
      } else {
        setSubscribeStatus('error');
      }
    } catch (error) {
      setSubscribeStatus('error');
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gradient-to-br from-teal-500 to-emerald-600 mx-6 mb-6 rounded-t-[40px]">
      {/* Contact Form Section */}
      <div id="contact" className="px-12 pt-20 pb-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h4 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Get in Touch
            </h4>
            <p className="text-white/90 text-lg">
              Have questions? We're here to help you secure your digital privacy.
            </p>
          </div>

          <form id="contact-form" data-readdy-form onSubmit={handleContactSubmit} className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-12">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-white font-semibold mb-2 text-sm">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 text-sm"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-white font-semibold mb-2 text-sm">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 text-sm"
                  placeholder="your@email.com"
                />
              </div>
            </div>
            <div className="mb-6">
              <label htmlFor="message" className="block text-white font-semibold mb-2 text-sm">Message</label>
              <textarea
                id="message"
                name="message"
                value={message}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setMessage(e.target.value);
                  }
                }}
                required
                rows={5}
                maxLength={500}
                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 resize-none text-sm"
                placeholder="How can we help you?"
              />
              <div className="text-right text-white/70 text-xs mt-1">{message.length}/500</div>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-white text-teal-600 rounded-lg font-bold hover:bg-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
            {submitStatus === 'success' && (
              <div className="mt-4 p-4 bg-white/20 border border-white/30 rounded-lg text-white text-center text-sm">
                Thank you! We'll get back to you soon.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="mt-4 p-4 bg-red-500/20 border border-red-300/30 rounded-lg text-white text-center text-sm">
                Please fill in all fields correctly.
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Footer Content */}
      <div className="px-12 pt-12 pb-8 border-t border-white/20">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          {/* Left Column */}
          <div>
            <h5 className="text-5xl font-light text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              BRZOGLAS
            </h5>
            <p className="text-white/80 mb-12">Secure VPN for Modern Privacy</p>
            
            <form id="newsletter-form" data-readdy-form onSubmit={handleSubscribe}>
              <input
                type="email"
                name="email"
                value={subscribeEmail}
                onChange={(e) => setSubscribeEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="w-full px-0 py-3 bg-transparent border-b border-white/30 text-white placeholder-white/50 focus:outline-none focus:border-white transition-colors mb-4 text-sm"
              />
              <button
                type="submit"
                className="w-full px-6 py-3.5 bg-white text-teal-600 rounded-full font-semibold hover:bg-gray-100 transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
              >
                Get Updates
                <i className="ri-arrow-right-line"></i>
              </button>
              {subscribeStatus === 'success' && (
                <div className="mt-3 text-white text-sm text-center">Subscribed successfully!</div>
              )}
              {subscribeStatus === 'error' && (
                <div className="mt-3 text-red-200 text-sm text-center">Please enter a valid email.</div>
              )}
            </form>
          </div>

          {/* Middle Column */}
          <div>
            <h6 className="text-sm uppercase tracking-wider text-white/70 mb-6">Quick Links</h6>
            <div className="space-y-4">
              <button onClick={() => scrollToSection('features')} className="block text-white hover:underline transition-all cursor-pointer text-left">
                Features
              </button>
              <button onClick={() => scrollToSection('about')} className="block text-white hover:underline transition-all cursor-pointer text-left">
                About
              </button>
              <button onClick={() => scrollToSection('contact')} className="block text-white hover:underline transition-all cursor-pointer text-left">
                Contact
              </button>
            </div>
          </div>

          {/* Right Column */}
          <div>
            <h6 className="text-sm uppercase tracking-wider text-white/70 mb-6">Contact</h6>
            <a href="tel:+12536837909" className="block text-white text-lg mb-4 hover:underline">
              +1 2536837909
            </a>
            <a href="mailto:contact@brzoglasdoo.com" className="block text-white/80 mb-8 hover:underline">
              contact@brzoglasdoo.com
            </a>
            
            <h6 className="text-sm uppercase tracking-wider text-white/70 mb-4">Location</h6>
            <address className="not-italic text-white/80 text-sm leading-relaxed">
              Put Zabic 65<br />
              21216 Kastel Novi<br />
              Croatia
            </address>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="pt-8 border-t border-white/20 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex gap-8">
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-white hover:underline text-sm cursor-pointer">
              LinkedIn
            </a>
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-white hover:underline text-sm cursor-pointer">
              Twitter
            </a>
          </div>
          
          <p className="text-white/70 text-sm text-center">
            © 2025 BRZOGLAS, d.o.o. All rights reserved
          </p>
          
          <div className="flex gap-6">
            <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-white hover:underline text-sm cursor-pointer whitespace-nowrap">
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
